import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "utils/redux/store";
import { request } from "utils/request";
import { REACT_APP_API_URL } from "utils/environmentVariables";
import { aiServiceFetch, isUsingAIService } from "utils/aiServiceRequest";
import {
  GenerateCompetencyFrameworkRequest,
  GenerateCompetencyFrameworkResponse,
  GenerateNinetyDayDevelopmentPlanRequest,
  GenerateNinetyDayDevelopmentPlanResponse,
  GeneratePerformanceReviewRequest,
  GeneratePerformanceReviewResponse,
  PerformanceAnalysisResponse,
} from "./types";
import { responseStatus } from "utils/types";

// ------------------ State Type/Structure ------------------
export interface PerformanceAnalysisState {
  performanceAnalysis: PerformanceAnalysisResponse | null;
  gettingPerformanceAnalysisStatus: responseStatus;
  generatingPerformanceReviewStatus: responseStatus;
  generatingCompetencyFrameworkStatus: responseStatus;
  generatingNinetyDayDevelopmentPlanStatus: responseStatus;
}

// ------------------ InitialState ------------------
const initialState: PerformanceAnalysisState = {
  performanceAnalysis: null,
  gettingPerformanceAnalysisStatus: "idle",
  generatingPerformanceReviewStatus: "idle",
  generatingCompetencyFrameworkStatus: "idle",
  generatingNinetyDayDevelopmentPlanStatus: "idle",
};

// ------------------ Asynchronous API calls ------------------
export const getPerformanceAnalysis = createAsyncThunk(
  "performanceAnalysis/getPerformanceAnalysis",
  async (userAccountId: number) => {
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/users/${userAccountId}/performanceAnalysis`;
    const response = (await request(requestUrl)) as PerformanceAnalysisResponse;
    return response;
  }
);

export const generatePerformanceReview = createAsyncThunk(
  "performanceAnalysis/generatePerformanceReview",
  async ({
    userAccountId,
    startDate,
    endDate,
    additionalContext,
  }: GeneratePerformanceReviewRequest) => {
    if (isUsingAIService()) {
      const data = await aiServiceFetch<{ data: GeneratePerformanceReviewResponse }>(
        "/v1/reports/performance/performanceReview",
        {
          body: {
            targetUserAccountId: userAccountId,
            startDate,
            endDate,
            additionalContext,
          },
        }
      );
      return data.data;
    }

    // Fallback to Laravel API
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/users/${userAccountId}/performanceReview`;
    const response = (await request(requestUrl, {
      method: "POST",
      body: JSON.stringify({
        startDate,
        endDate,
        additionalContext,
      }),
    })) as GeneratePerformanceReviewResponse;
    return response;
  }
);

export const generateCompetencyFramework = createAsyncThunk(
  "performanceAnalysis/generateCompetencyFramework",
  async ({
    userAccountId,
    jobTitle,
    additionalContext,
  }: GenerateCompetencyFrameworkRequest) => {
    if (isUsingAIService()) {
      const data = await aiServiceFetch<{ data: GenerateCompetencyFrameworkResponse }>(
        "/v1/reports/performance/competencyFramework",
        {
          body: {
            targetUserAccountId: userAccountId,
            jobTitle,
            additionalContext,
          },
        }
      );
      return data.data;
    }

    // Fallback to Laravel API
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/users/${userAccountId}/competencyFramework`;
    const response = (await request(requestUrl, {
      method: "POST",
      body: JSON.stringify({
        jobTitle,
        additionalContext,
      }),
    })) as GenerateCompetencyFrameworkResponse;
    return response;
  }
);

export const generateNinetyDayDevelopmentPlan = createAsyncThunk(
  "performanceAnalysis/generateNinetyDayDevelopmentPlan",
  async ({
    userAccountId,
    additionalContext,
  }: GenerateNinetyDayDevelopmentPlanRequest) => {
    if (isUsingAIService()) {
      const data = await aiServiceFetch<{ data: GenerateNinetyDayDevelopmentPlanResponse }>(
        "/v1/reports/performance/ninetyDayDevelopmentPlan",
        {
          body: {
            targetUserAccountId: userAccountId,
            additionalContext,
          },
        }
      );
      return data.data;
    }

    // Fallback to Laravel API
    const requestUrl = `${REACT_APP_API_URL}/talentInsights/users/${userAccountId}/ninetyDayDevelopmentPlan`;
    const response = (await request(requestUrl, {
      method: "POST",
      body: JSON.stringify({
        additionalContext,
      }),
    })) as GenerateNinetyDayDevelopmentPlanResponse;
    return response;
  }
);
// ------------------ Beginning of Slice Definition ------------------
export const performanceAnalysisSlice = createSlice({
  name: "performanceAnalysis",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getPerformanceAnalysis.pending, (state) => {
        state.gettingPerformanceAnalysisStatus = "loading";
      })
      .addCase(getPerformanceAnalysis.fulfilled, (state, action) => {
        state.performanceAnalysis = action.payload;
        state.gettingPerformanceAnalysisStatus = "succeeded";
      })
      .addCase(getPerformanceAnalysis.rejected, (state) => {
        state.gettingPerformanceAnalysisStatus = "failed";
      })
      .addCase(generatePerformanceReview.pending, (state) => {
        state.generatingPerformanceReviewStatus = "loading";
      })
      .addCase(generatePerformanceReview.fulfilled, (state, action) => {
        state.generatingPerformanceReviewStatus = "succeeded";
        if (state.performanceAnalysis) {
          state.performanceAnalysis.performanceAnalysis.performanceReview =
            action.payload.performanceReview;
        }
      })
      .addCase(generatePerformanceReview.rejected, (state) => {
        state.generatingPerformanceReviewStatus = "failed";
      })
      .addCase(generateCompetencyFramework.pending, (state) => {
        state.generatingCompetencyFrameworkStatus = "loading";
      })
      .addCase(generateCompetencyFramework.fulfilled, (state, action) => {
        state.generatingCompetencyFrameworkStatus = "succeeded";
        if (state.performanceAnalysis) {
          state.performanceAnalysis.performanceAnalysis.competencyFramework =
            action.payload.competencyFramework;
        }
      })
      .addCase(generateCompetencyFramework.rejected, (state) => {
        state.generatingCompetencyFrameworkStatus = "failed";
      })
      .addCase(generateNinetyDayDevelopmentPlan.pending, (state) => {
        state.generatingNinetyDayDevelopmentPlanStatus = "loading";
      })
      .addCase(generateNinetyDayDevelopmentPlan.fulfilled, (state, action) => {
        state.generatingNinetyDayDevelopmentPlanStatus = "succeeded";
        if (state.performanceAnalysis) {
          state.performanceAnalysis.performanceAnalysis.ninetyDayDevelopmentPlan =
            action.payload.ninetyDayDevelopmentPlan;
        }
      })
      .addCase(generateNinetyDayDevelopmentPlan.rejected, (state) => {
        state.generatingNinetyDayDevelopmentPlanStatus = "failed";
      });
  },
});

// ------------------ Selectors ------------------
export const selectPerformanceAnalysisResponse = (state: RootState) =>
  state.performanceAnalysis.performanceAnalysis;
export const selectGettingPerformanceAnalysisStatus = (state: RootState) =>
  state.performanceAnalysis.gettingPerformanceAnalysisStatus;
export const selectGeneratingPerformanceReviewStatus = (state: RootState) =>
  state.performanceAnalysis.generatingPerformanceReviewStatus;
export const selectGeneratingCompetencyFrameworkStatus = (state: RootState) =>
  state.performanceAnalysis.generatingCompetencyFrameworkStatus;
export const selectGeneratingNinetyDayDevelopmentPlanStatus = (
  state: RootState
) => state.performanceAnalysis.generatingNinetyDayDevelopmentPlanStatus;

export default performanceAnalysisSlice.reducer;
